import React, { useState, useEffect, useRef } from "react";
import CloseIcon from "../../../components/ui/icons/CloseIcon";
import ShieldIcon from "../../../components/ui/icons/ShieldIcon";

interface ConsentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  onDecline: () => void;
}

type ConsentChoice = "accept" | "decline" | null;

const ConsentModal: React.FC<ConsentModalProps> = ({
  isOpen,
  onClose,
  onAccept,
  onDecline,
}) => {
  const [choice, setChoice] = useState<ConsentChoice>(null);
  const [submitting, setSubmitting] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setChoice(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (cardRef.current && !cardRef.current.contains(e.target as Node))
      onClose();
  };

  const handleContinue = async () => {
    if (!choice || submitting) return;
    setSubmitting(true);
    if (choice === "accept") {
      onAccept();
    } else {
      await new Promise((r) => setTimeout(r, 600));
      onDecline();
    }
    setSubmitting(false);
  };

  return (
    <>
      <style>{`
        @keyframes backdropIn { from { opacity:0 } to { opacity:1 } }
        @keyframes modalIn {
          from { opacity:0; transform:translateY(24px) scale(0.97) }
          to   { opacity:1; transform:translateY(0) scale(1) }
        }
        .consent-backdrop { animation: backdropIn 0.22s ease both; }
        .consent-card     { animation: modalIn 0.32s cubic-bezier(0.22,1,0.36,1) 0.05s both; }
      `}</style>

      {/* ── Backdrop ── */}
      <div
        className="consent-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4 sm:p-6 bg-[rgba(26,28,60,0.55)] backdrop-blur-sm"
        onClick={handleBackdropClick}
      >
        {/* ── Card ── */}
        <div
          ref={cardRef}
          className="consent-card relative w-full max-w-[560px] bg-white rounded-2xl overflow-hidden shadow-[0_24px_64px_rgba(38,41,94,0.18),0_4px_16px_rgba(0,0,0,0.06)]"
        >
          {/* ── Close button ── */}
          <button
            onClick={onClose}
            aria-label="Close consent modal"
            className="absolute top-4 right-4 z-10 flex items-center justify-center w-[34px] h-[34px] rounded-full text-meta bg-transparent border-none cursor-pointer transition-colors duration-150 hover:bg-[#f0f3fa] hover:text-dark-blue"
          >
            <CloseIcon />
          </button>

          {/* ── Header ── */}
          <div className="px-5 sm:px-8 pt-6 sm:pt-8 pb-4 sm:pb-6 text-center border-b border-[#f0f3fa]">
            {/* Shield badge */}
            <div className="inline-flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-full mb-3 sm:mb-5 bg-gradient-to-br from-[#eef2ff] to-[#dde5f9] text-dark-blue">
              <ShieldIcon />
            </div>
            <h2 className="font-bold text-dark-blue font-primary text-base sm:text-[clamp(18px,2.5vw,22px)] leading-tight mb-1">
              Welcome to Team Trust Accelerator,
            </h2>
            <h2 className="font-bold font-primary text-base sm:text-[clamp(18px,2.5vw,22px)] leading-tight text-dark-blue">
              powered by Good Leadership
            </h2>
          </div>

          {/* ── Body ── */}
          <div className="px-5 sm:px-8 py-5 sm:py-6">
            <p className="text-meta font-primary text-xs sm:text-sm leading-relaxed mb-5 sm:mb-7 text-center">
              As your access to this assessment was provided for you by your
              organization, Good Leadership will share your results with both{" "}
              <strong className="text-dark-blue font-semibold">you</strong> and{" "}
              <strong className="text-dark-blue font-semibold">
                the organization
              </strong>{" "}
              to facilitate development and team trust acceleration.
            </p>

            {/* ── Choices ── */}
            <div className="flex flex-col gap-2 sm:gap-3">
              {/* Accept option */}
              <label
                className={[
                  "flex items-center gap-3 sm:gap-4 rounded-xl border-2 px-3 sm:px-5 py-3 sm:py-4 cursor-pointer transition-all duration-150",
                  choice === "accept"
                    ? "border-blue bg-[#f0f4ff] shadow-[0_0_0_3px_rgba(59,81,156,0.1)]"
                    : "border-[#e8edf5] bg-white",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="consent"
                  value="accept"
                  checked={choice === "accept"}
                  onChange={() => setChoice("accept")}
                  className="sr-only"
                />
                {/* Custom radio dot */}
                <span
                  className={[
                    "flex-shrink-0 w-[18px] h-[18px] sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150",
                    choice === "accept"
                      ? "border-[#3b519c] bg-[#3b519c]"
                      : "border-[#c5cfe0] bg-white",
                  ].join(" ")}
                >
                  {choice === "accept" && (
                    <span className="block w-[6px] h-[6px] sm:w-[7px] sm:h-[7px] rounded-full bg-white" />
                  )}
                </span>
                <div className="min-w-0">
                  <p
                    className={[
                      "font-semibold font-primary text-xs sm:text-sm",
                      choice === "accept" ? "text-dark-blue" : "text-[#4a5568]",
                    ].join(" ")}
                  >
                    I accept
                  </p>
                  <p className="text-meta font-primary text-[11px] sm:text-xs leading-snug sm:leading-relaxed mt-0.5">
                    I agree to share my results with Good Leadership and my
                    organization. Continue to the survey.
                  </p>
                </div>
              </label>

              {/* Decline option */}
              <label
                className={[
                  "flex items-center gap-3 sm:gap-4 rounded-xl border-2 px-3 sm:px-5 py-3 sm:py-4 cursor-pointer transition-all duration-150",
                  choice === "decline"
                    ? "border-[#e05b6a] bg-[#fff5f6] shadow-[0_0_0_3px_rgba(224,91,106,0.1)]"
                    : "border-[#e8edf5] bg-white",
                ].join(" ")}
              >
                <input
                  type="radio"
                  name="consent"
                  value="decline"
                  checked={choice === "decline"}
                  onChange={() => setChoice("decline")}
                  className="sr-only"
                />
                <span
                  className={[
                    "flex-shrink-0 w-[18px] h-[18px] sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150",
                    choice === "decline"
                      ? "border-[#e05b6a] bg-[#e05b6a]"
                      : "border-[#c5cfe0] bg-white",
                  ].join(" ")}
                >
                  {choice === "decline" && (
                    <span className="block w-[6px] h-[6px] sm:w-[7px] sm:h-[7px] rounded-full bg-white" />
                  )}
                </span>
                <div className="min-w-0">
                  <p
                    className={[
                      "font-semibold font-primary text-xs sm:text-sm",
                      choice === "decline"
                        ? "text-[#c0394a]"
                        : "text-[#4a5568]",
                    ].join(" ")}
                  >
                    I do not accept
                  </p>
                  <p className="text-meta font-primary text-[11px] sm:text-xs leading-snug sm:leading-relaxed mt-0.5">
                    Good Leadership will follow up with you by email to discuss
                    your concerns.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="px-5 sm:px-8 pb-6 sm:pb-8 pt-4 sm:pt-5 border-t border-[#f0f3fa]">
            <button
              onClick={handleContinue}
              disabled={!choice || submitting}
              className={[
                "btn btn-md btn-normal w-full font-primary font-semibold text-[15px] transition-all duration-150",
                !choice
                  ? "opacity-45 cursor-not-allowed"
                  : "opacity-100 cursor-pointer",
                choice === "decline" ? "!bg-[#e05b6a]" : "",
              ].join(" ")}
            >
              {submitting
                ? "Please wait…"
                : choice === "decline"
                  ? "Submit & notify Good Leadership"
                  : "Continue to survey"}
            </button>
            <p className="text-center text-meta font-primary text-[11px] mt-3">
              You must make a selection to continue.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ConsentModal;
